const YT = "https://www.googleapis.com/youtube/v3";

// ── Server-side proxy caller ──────────────────────────────────────────────────
// Replaces direct YouTube API calls. The API key stays in Firestore/server.
// `getToken` should be a function returning a Promise<string> Firebase ID token.
export async function ytProxy(action, channelId, params, getToken) {
  const token = await getToken();
  const r = await fetch('/.netlify/functions/youtube-proxy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ action, channelId, ...params }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || `YouTube proxy error ${r.status}`);
  return data.result;
}



export async function ytGet(path, params, apiKey) {
  const url = new URL(YT + path);
  Object.entries({ ...params, key: apiKey }).forEach(([k, v]) => url.searchParams.set(k, v));
  const r = await fetch(url.toString());
  const data = await r.json();
  if (!r.ok) throw new Error(data.error?.message || `YouTube API error ${r.status}`);
  return data;
}

export async function fetchChannelInfo(apiKey, input) {
  const isHandle = input.startsWith("@"), isId = input.startsWith("UC");
  const params = {
    part: "snippet,contentDetails,statistics,brandingSettings",
    ...(isHandle ? { forHandle: input.slice(1) } : isId ? { id: input } : { forUsername: input })
  };
  const data = await ytGet("/channels", params, apiKey);
  if (!data.items?.length) throw new Error("Channel not found. Try UC... ID or @handle.");
  return data.items[0];
}

export async function fetchVideoIds(apiKey, playlistId, limit = 0, onProgress) {
  let token = null, ids = [], page = 0;
  const maxPages = limit === 0 ? Infinity : Math.ceil(limit / 50);
  do {
    const data = await ytGet("/playlistItems", {
      part: "contentDetails", playlistId, maxResults: 50,
      ...(token ? { pageToken: token } : {})
    }, apiKey);
    ids.push(...data.items.map(i => i.contentDetails.videoId));
    token = data.nextPageToken;
    onProgress?.(ids.length, data.pageInfo?.totalResults);
    if (++page >= maxPages) break;
    if (limit > 0 && ids.length >= limit) break;
  } while (token);
  return limit > 0 ? ids.slice(0, limit) : ids;
}

export async function fetchVideoDetails(apiKey, ids) {
  const out = [];
  for (let i = 0; i < ids.length; i += 50) {
    const data = await ytGet("/videos", {
      part: "snippet,statistics,contentDetails,status,topicDetails,liveStreamingDetails",
      id: ids.slice(i, i + 50).join(",")
    }, apiKey);
    out.push(...(data.items || []));
  }
  return out;
}

export async function fetchNewVideosSince(apiKey, playlistId, sinceDate) {
  let token = null, ids = [], done = false;
  do {
    const data = await ytGet("/playlistItems", {
      part: "contentDetails", playlistId, maxResults: 50,
      ...(token ? { pageToken: token } : {})
    }, apiKey);
    for (const item of data.items) {
      const published = new Date(item.contentDetails.videoPublishedAt);
      if (published <= new Date(sinceDate)) { done = true; break; }
      ids.push(item.contentDetails.videoId);
    }
    token = data.nextPageToken;
    if (!token) break;
  } while (!done);
  return ids;
}

export const parseDuration = iso => {
  const m = iso?.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/) || [];
  return (parseInt(m[1] || 0) * 3600) + (parseInt(m[2] || 0) * 60) + parseInt(m[3] || 0);
};

export const formatDuration = s => {
  if (!s) return "0:00";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sc = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2,"0")}:${String(sc).padStart(2,"0")}`
    : `${m}:${String(sc).padStart(2,"0")}`;
};

export const formatNumber = n => {
  const v = parseInt(n || 0);
  if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + "K";
  return v.toLocaleString();
};

export const formatDate = iso => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

export const normalizeVideo = v => ({
  id: v.id,
  title: v.snippet.title,
  description: v.snippet.description || "",
  publishedAt: v.snippet.publishedAt,
  channelTitle: v.snippet.channelTitle,
  thumbnailDefault: v.snippet.thumbnails?.default?.url || "",
  thumbnailMedium: v.snippet.thumbnails?.medium?.url || "",
  thumbnailHigh: v.snippet.thumbnails?.high?.url || "",
  tags: v.snippet.tags || [],
  categoryId: v.snippet.categoryId || "",
  defaultLanguage: v.snippet.defaultLanguage || "",
  duration: v.contentDetails?.duration || "",
  durationSeconds: parseDuration(v.contentDetails?.duration),
  isShort: parseDuration(v.contentDetails?.duration) <= 60,
  hasCaption: v.contentDetails?.caption === "true",
  licensedContent: v.contentDetails?.licensedContent || false,
  privacyStatus: v.status?.privacyStatus || "public",
  madeForKids: v.status?.madeForKids || false,
  embeddable: v.status?.embeddable !== false,
  viewCount: parseInt(v.statistics?.viewCount || 0),
  likeCount: parseInt(v.statistics?.likeCount || 0),
  commentCount: parseInt(v.statistics?.commentCount || 0),
  topicCategories: v.topicDetails?.topicCategories || [],
  liveBroadcastContent: v.snippet?.liveBroadcastContent || "none",
  wasLive: v.snippet?.liveBroadcastContent === "completed",
  actualStartTime: v.liveStreamingDetails?.actualStartTime || null,
  actualEndTime: v.liveStreamingDetails?.actualEndTime || null,
  scheduledStartTime: v.liveStreamingDetails?.scheduledStartTime || null,
  updatedAt: new Date().toISOString(),
});


// ── Playlist fetching ─────────────────────────────────────────────────────────

// Fetch all playlists for a channel (1 quota unit per 50 playlists)
export async function fetchChannelPlaylists(apiKey, channelId) {
  const playlists = [];
  let pageToken = "";
  do {
    const params = { channelId, part: "snippet,contentDetails", maxResults: 50 };
    if (pageToken) params.pageToken = pageToken;
    const data = await ytGet("/playlists", params, apiKey);
    (data.items || []).forEach(p => playlists.push({
      id: p.id,
      title: p.snippet.title,
      description: p.snippet.description || "",
      videoCount: p.contentDetails?.itemCount || 0,
      publishedAt: p.snippet.publishedAt,
    }));
    pageToken = data.nextPageToken || "";
  } while (pageToken);
  return playlists;
}

// Fetch video IDs for a specific playlist (1 quota unit per 50 videos)
export async function fetchPlaylistVideoIds(apiKey, playlistId) {
  const ids = new Set();
  let pageToken = "";
  do {
    const params = { playlistId, part: "contentDetails", maxResults: 50 };
    if (pageToken) params.pageToken = pageToken;
    const data = await ytGet("/playlistItems", params, apiKey);
    (data.items || []).forEach(item => {
      const vid = item.contentDetails?.videoId;
      if (vid) ids.add(vid);
    });
    pageToken = data.nextPageToken || "";
  } while (pageToken);
  return [...ids];
}

// Build a full video→playlists map for a channel
// Returns: { videoId: [{ id, title }], ... }
export async function buildVideoPlaylistMap(apiKey, channelId, playlists) {
  const map = {};
  // Only fetch for playlists with reasonable size (skip huge auto-generated ones)
  const relevant = playlists.filter(p => p.videoCount > 0 && p.videoCount < 2000);
  for (const playlist of relevant) {
    const ids = await fetchPlaylistVideoIds(apiKey, playlist.id);
    ids.forEach(vid => {
      if (!map[vid]) map[vid] = [];
      map[vid].push({ id: playlist.id, title: playlist.title });
    });
  }
  return map;
}

// ── OAuth-gated calls (require channel owner access token) ────────────────────

// Fetch caption tracks for a video (50 quota units)
export async function fetchCaptionTracks(videoId, accessToken) {
  const url = new URL("https://www.googleapis.com/youtube/v3/captions");
  url.searchParams.set("part", "snippet");
  url.searchParams.set("videoId", videoId);
  const r = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${accessToken}` }
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error?.message || "Caption list failed");
  return data.items || [];
}

// Download caption file and parse to plain text (200 quota units)
export async function downloadCaption(captionId, accessToken) {
  const url = new URL(`https://www.googleapis.com/youtube/v3/captions/${captionId}`);
  url.searchParams.set("tfmt", "srt");
  const r = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${accessToken}` }
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error?.message || "Caption download failed");
  }
  const srt = await r.text();
  return parseSRT(srt);
}

// Parse SRT format to clean plain text with optional timestamps
function parseSRT(srt) {
  return srt
    .split(/\n\n+/)
    .map(block => {
      const lines = block.trim().split("\n");
      // Skip index line and timestamp line, keep text
      return lines.slice(2).join(" ");
    })
    .filter(Boolean)
    .join(" ")
    .replace(/<[^>]+>/g, "") // strip HTML tags
    .trim();
}

// Fetch top comments for a video (1 quota unit)
export async function fetchComments(apiKey, videoId, maxResults = 100) {
  const out = [];
  let token = null;
  do {
    const data = await ytGet("/commentThreads", {
      part: "snippet",
      videoId,
      maxResults: Math.min(maxResults - out.length, 100),
      order: "relevance",
      ...(token ? { pageToken: token } : {})
    }, apiKey);
    out.push(...(data.items || []).map(item => ({
      id: item.id,
      text: item.snippet.topLevelComment.snippet.textDisplay,
      author: item.snippet.topLevelComment.snippet.authorDisplayName,
      likes: item.snippet.topLevelComment.snippet.likeCount,
      published: item.snippet.topLevelComment.snippet.publishedAt,
      replyCount: item.snippet.totalReplyCount,
    })));
    token = data.nextPageToken;
    if (out.length >= maxResults) break;
  } while (token);
  return out;
}
